function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
var publishStandardEvent = function publishStandardEvent(eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
var standardEventCartFromAjax = function standardEventCartFromAjax(cart) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !cart || !cart.token || !cart.items) return null;
  return SE.CartViewEvent.createCartFromAjaxResponse(cart);
};
window.recentlyViewedIds = [];

const FETCH_CONFIG = {
  method: 'POST',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/javascript'
  }
};

const OverlayManager = {
  stack: [],
  beforeCloseHandlers: new Map(),
  lastFocused: new Map(),
  cc: null,
  _trapHandler: null,
  focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',

  getCC() {
    if (!this.cc) {
      this.cc = document.querySelector('.click-capture');
    }
    return this.cc;
  },

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.stack.length) return;
    const panel = this.stack[this.stack.length - 1];
    const focusable = Array.from(panel.querySelectorAll(this.focusableSelector)).filter((el) => el.getClientRects().length);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = panel.contains(document.activeElement);
    if (e.shiftKey) {
      if (!inside || document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else if (!inside || document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  },

  updateCCZIndex() {
    let cc = this.getCC();
    if (!cc) return;
    if (this.stack.length > 1) {
      let topPanel = this.stack[this.stack.length - 1];
      let topZ = parseInt(getComputedStyle(topPanel).zIndex, 10) || 90;
      cc.style.zIndex = topZ - 1;
    } else {
      cc.style.zIndex = '';
    }
  },

  open(panel) {
    if (!panel) return;
    // If already in stack, move to top
    const index = this.stack.indexOf(panel);
    if (index === -1) {
      this.lastFocused.set(panel, document.activeElement);
    } else {
      this.stack.splice(index, 1);
    }
    this.stack.push(panel);
    if (this.stack.length === 1) {
      if (!this._trapHandler) {
        this._trapHandler = this.trapFocus.bind(this);
      }
      document.addEventListener('keydown', this._trapHandler);
    }
    panel.classList.add('active');
    panel.inert = false;
    document.body.classList.add('open-cc');
    this.updateCCZIndex();
    panel.querySelector('.side-panel-close')?.focus();
    dispatchCustomEvent('panel:open', { panel });
  },

  close(panel) {
    if (!panel) return;
    const index = this.stack.indexOf(panel);
    if (index > -1) this.stack.splice(index, 1);
    if (!this.stack.length) {
      document.body.classList.remove('open-cc');
      if (this._trapHandler) {
        document.removeEventListener('keydown', this._trapHandler);
      }
    }
    this.updateCCZIndex();
    panel.classList.remove('active');
    panel.inert = true;
    dispatchCustomEvent('panel:closed', { panel });

    const restoreTarget = this.lastFocused.get(panel);
    this.lastFocused.delete(panel);
    if (restoreTarget && document.body.contains(restoreTarget) && !this.stack.length) {
      restoreTarget.focus();
    }

    const handler = this.beforeCloseHandlers.get(panel);
    if (handler) handler();
  },

  closeLast() {
    const panel = this.stack[this.stack.length - 1];
    if (panel) this.close(panel);
  },

  registerBeforeClose(panel, handler) {
    this.beforeCloseHandlers.set(panel, handler);
  },

  closeAll() {
    while (this.stack.length) {
      this.closeLast();
    }
  },

  isOpen(panel) {
    return this.stack.includes(panel);
  },

  hasOpen() {
    return this.stack.length > 0;
  }
};

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));

      this.validateQtyRules();
      this.classList.add('buttons_added');
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }

      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}


/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let image_container = this.querySelector('.product-featured-image');
      let cachedWidth = 0;
      let currentIndex = -1;
      let rafId = null;

      const updateHover = (e) => {
        let l = e.offsetX;
        let prc = l / cachedWidth;
        let sel = Math.floor(prc * image_length);
        // Touch events have no offsetX, so sel is NaN there — it slips past
        // the range checks (NaN comparisons are all false) and would index
        // images[NaN]. Non-finite means nothing to hover.
        if (!Number.isFinite(sel) || sel === currentIndex || sel < 0 || sel >= image_length) return;
        if (currentIndex >= 0) {
          images[currentIndex].classList.remove('hover');
          if (nav.length) nav[currentIndex].classList.remove('active');
        }
        currentIndex = sel;
        images[sel].classList.add('hover');
        if (nav.length) nav[sel].classList.add('active');
      };

      const mousemove = (e) => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          updateHover(e);
        });
      };

      const mouseenter = (e) => {
        cachedWidth = image_container.getBoundingClientRect().width;
        updateHover(e);
      };

      const mouseleave = () => {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        if (currentIndex >= 0) {
          images[currentIndex].classList.remove('hover');
          if (nav.length) nav[currentIndex].classList.remove('active');
          currentIndex = -1;
        }
      };

      if (image_container) {
        image_container.addEventListener('touchstart', mouseenter, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mouseenter, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      if (document.readyState === 'complete') {
        images.forEach(function (image) {
          lazySizes.loader.unveil(image);
        });
      } else {
        window.addEventListener('load', () => {
          images.forEach(function (image) {
            lazySizes.loader.unveil(image);
          });
        }, { once: true });
      }
    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';

      const unveilSwatches = () => {
        swatch_list.forEach((swatch) => {
          let img = new Image();
          img.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(img);
        });
      };
      if (document.readyState === 'complete') {
        unveilSwatches();
      } else {
        window.addEventListener('load', unveilSwatches, { once: true });
      }

      swatches.addEventListener('mouseover', (e) => {
        let swatch = e.target.closest('.product-card-swatch');
        if (!swatch) return;
        let active = swatches.querySelector('.product-card-swatch.active');
        if (active) active.classList.remove('active');
        swatch.classList.add('active');
        if (image) {
          image.setAttribute('srcset', swatch.dataset.srcset || org_srcset);
        }
      });

      swatches.addEventListener('click', (e) => {
        let swatch = e.target.closest('.product-card-swatch');
        if (!swatch) return;
        e.preventDefault();
        window.location.href = swatch.dataset.href;
      });
    }
    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }

    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) {
        return;
      }
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      let formData = new FormData(this.form);

      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, { ...FETCH_CONFIG, body: formData })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const elementToReplace = el.querySelector(section.selector) || el;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));

      let cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        let productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer && OverlayManager.isOpen(productDrawer)) {
          OverlayManager.close(productDrawer);
        }
        cartDrawer.open();
      }

    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}

/**
 *  @class
 *  @function ProductCardSmall
 */
if (!customElements.get('product-card-small')) {
  class ProductCardSmall extends HTMLElement {
    constructor() {
      super();

      this.quick_add_enabled = this.classList.contains('quick-add-to-card--true');
      this.button = this.querySelector('button');
      this.id = this.dataset.id;
      this.url = this.dataset.url;
    }
    connectedCallback() {
      if (this.quick_add_enabled) {
        this.setEventListeners();
      }
    }
    setEventListeners() {
      this.button.addEventListener('click', this.addToCart.bind(this));
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    addToCart() {
      this.button.classList.add('loading');
      if (!this.quick_add_enabled) {
        // quick view
      }
      this.button.setAttribute('aria-disabled', true);

      let formData = new FormData(this.form);

      formData.append('id', this.id);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      fetch(`${theme.routes.cart_add_url}`, { ...FETCH_CONFIG, body: formData })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.button.classList.remove('loading');
          this.button.removeAttribute('aria-disabled');
        });
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const elementToReplace = el.querySelector(section.selector) || el;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));
      let cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        let productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer && OverlayManager.isOpen(productDrawer)) {
          OverlayManager.close(productDrawer);
        }
        cartDrawer.open();
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card-small', ProductCardSmall);
}

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {

    connectedCallback() {
      // Each instance gets its own click handler
      this.addEventListener('click', (e) => this.close_panel(e));

      // Global listeners only once
      if (!PanelClose._initialized) {
        PanelClose._initialized = true;
        document.addEventListener('keyup', (e) => {
          if (e.code && e.code.toUpperCase() === 'ESCAPE') {
            OverlayManager.closeAll();
          }
        });
        let cc = document.querySelector('.click-capture');
        if (cc) {
          cc.addEventListener('click', () => {
            OverlayManager.closeLast();
          });
        }
      }
    }

    close_panel(e) {
      e.preventDefault();
      let panel = e.target.closest('.side-panel');
      if (panel) {
        OverlayManager.close(panel);
      }
    }
  }
  PanelClose._initialized = false;
  customElements.define('side-panel-close', PanelClose);
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');


      // Add functionality to buttons
      button?.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', (event) => {
        this.refresh();
      });

      this.addEventListener('change', this.debouncedOnChange.bind(this));

    }
    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }
    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`).classList.add('thb-loading');
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });
      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const el = document.getElementById(section.id);
            if (!el) return;
            const elementToReplace = el.querySelector(section.selector) || el;

            if (parsedState.sections) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          dispatchCustomEvent('line-item:change:end', {
            quantity: quantity,
            cart: parsedState
          });
          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        });
    }
    open() {
      OverlayManager.open(this);
      dispatchCustomEvent('cart-drawer:open');
    }
    refresh() {
      let sections = 'cart-drawer,cart-bubble';
      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const el = document.getElementById(section.id);
            if (!el) return;
            const elementToReplace = el.querySelector(section.selector) || el;

            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
          }));

        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}


/**
 *  @class
 *  @function CustomSelect
 */
if (!customElements.get('custom-select')) {
  class CustomSelect extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.detailsements = {
        form: this.closest('form'),
        input: this.querySelector('input'),
        button: this.querySelector('.custom-select--button'),
        panel: this.querySelector('.custom-select--list'),
      };
      this.autoClose = this.dataset.autoClose;
      this.detailsements.button.addEventListener('click', this.toggleSelector.bind(this));
      this.detailsements.button.addEventListener('focusout', this.closeSelector.bind(this));
      this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

      this.detailsements.panel?.querySelectorAll('button').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));
    }

    hidePanel() {
      this.detailsements.button.setAttribute('aria-expanded', 'false');
      this.classList.remove('custom-select--active');
    }

    onContainerKeyUp(event) {
      if (event.code.toUpperCase() !== 'ESCAPE') return;

      this.hidePanel();
      this.detailsements.button.focus();
    }

    onItemClick(event) {
      event.preventDefault();
      this.detailsements.input.value = event.currentTarget.dataset.value;
      this.detailsements.input.dispatchEvent(new Event('change'));
      this.detailsements.button.querySelector('.custom-select--text').textContent = event.currentTarget.textContent;
      if (this.detailsements.form) {
        this.detailsements.form.dispatchEvent(new Event('input'));
      }
      if (this.autoClose) {
        this.classList.remove('custom-select--active');
      }
    }

    toggleSelector(event) {
      event.preventDefault();
      this.classList.toggle('custom-select--active');
      if (this.classList.contains('custom-select--active')) {
        this.detailsements.button.focus();
        this.detailsements.button.setAttribute('aria-expanded', (this.detailsements.button.getAttribute('aria-expanded') === 'false').toString());
      } else {
        this.hidePanel();
      }
    }

    closeSelector(event) {
      const shouldClose = event.relatedTarget && event.relatedTarget.classList.contains('custom-select--button');
      if (event.relatedTarget === null || shouldClose) {
        this.hidePanel();
      }
    }
  }
  customElements.define('custom-select', CustomSelect);
}
/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.inputs = this.form.querySelectorAll('input[name="locale_code"], input[name="country_code"]');
      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 200);
      this.inputs.forEach(item => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
    }
    onSubmitHandler(event) {
      if (this.form) this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    disconnectedCallback() {

    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
          e.preventDefault();
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.setListeners();
    }
    setListeners() {
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @class
 *  @function ProductRecommendations
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

  }
  fetchProducts() {
    fetch(this.dataset.url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        this.classList.add('product-recommendations--loaded');

      })
      .catch(e => {
        console.error(e);
      });
  }
  connectedCallback() {
    this.fetchProducts();
  }
}

customElements.define('product-recommendations', ProductRecommendations);

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  const _cache = new Map();

  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.drawerContent = this.drawer.querySelector('#Product-Drawer-Content');

      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      OverlayManager.registerBeforeClose(this.drawer, () => {
        document.body.classList.remove('open-cc--product');
        setTimeout(() => {
          if (!this.drawer.classList.contains('active')) {
            this.drawerContent.innerHTML = '';
          }
        }, 260);
      });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }
    _onClick(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle;
      if (!productHandle || this.classList.contains('loading')) {
        return;
      }

      let href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;
      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');

      if (_cache.has(productHandle)) {
        this.renderQuickview(_cache.get(productHandle), href, productHandle);
        return;
      }

      this.classList.add('loading');
      fetch(href)
        .then((response) => response.text())
        .then((text) => {
          this.classList.remove('loading');
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content').innerHTML;

          _cache.set(productHandle, sectionInnerHTML);
          this.renderQuickview(sectionInnerHTML, href, productHandle);
        })
        .catch(() => {
          this.classList.remove('loading');
        });
    }
    _reloadScripts(container) {
      let scripts = container.querySelectorAll('script');
      let head = document.head;
      scripts.forEach((oldScript) => {
        let newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        if (oldScript.src) {
          if (document.querySelector(`script[src="${oldScript.src}"]`)) {
            return;
          }
        } else {
          newScript.textContent = oldScript.textContent;
        }
        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        } else {
          head.appendChild(newScript);
        }
      });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (!sectionInnerHTML) {
        return;
      }

      let drawerContent = this.drawer.querySelector('#Product-Drawer-Content');
      drawerContent.innerHTML = sectionInnerHTML;

      this._reloadScripts(drawerContent);

      OverlayManager.open(this.drawer);
      document.body.classList.add('open-cc--product');

      requestAnimationFrame(() => {
        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
        let slider = this.drawer.querySelector('#Product-Slider');
        if (slider) {
          slider.reInit();
        }
        window.dispatchEvent(new Event('resize'));
      });

      dispatchCustomEvent('quick-view:open', {
        productUrl: href,
        productHandle: productHandle
      });

      addIdToRecentlyViewed(productHandle);
    }
  }
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function AnimatedMarkers
 */
class AnimatedMarkers {

  constructor() {
    // Slideshow slide-heading markers are drawn by each slide's entrance timeline instead
    this.markers = [...document.querySelectorAll('.svg-marker path')].filter((marker) => !marker.closest('.slideshow__slide-heading'));
    this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';

    if (this.animations_enabled && this.markers.length) {
      this.setupEventListeners();
    }
  }
  setupEventListeners() {
    this.markers.forEach((marker, i) => {
      gsap.from(marker, {
        duration: 1,
        ease: 'power4.inOut',
        drawSVG: "0%",
        scrollTrigger: {
          trigger: marker,
          start: 'top 70%',
          end: 'bottom 80%'
        }
      });
    });
  }

}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds != 'undefined' && recentIds != null) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

document.addEventListener('DOMContentLoaded', () => {

  if (typeof AnimatedMarkers !== 'undefined') {
    new AnimatedMarkers();
  }

});
/**
 *  Standard events adapters
 *  Translate the theme's internal events into Shopify standard events
 *  (https://shopify.dev/docs/storefronts/themes/best-practices/standard-events).
 *  Progressive enhancement only: publishStandardEvent no-ops when the
 *  standard-events module is unavailable, and commerce flows never depend
 *  on anything here.
 */
(function () {
  document.addEventListener('cart:item-added', (event) => {
    const item = event.detail && event.detail.product;
    const lines = item && item.variant_id ? [{
      merchandiseId: item.variant_id,
      quantity: item.quantity || 1
    }] : undefined;
    publishStandardEvent('CartLinesUpdateEvent', {
      action: 'add',
      lines: lines,
      promise: Promise.resolve({ cart: null })
    });
  });

  document.addEventListener('line-item:change:end', (event) => {
    const detail = event.detail || {};
    const quantity = parseInt(detail.quantity, 10);
    publishStandardEvent('CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      promise: Promise.resolve({ cart: standardEventCartFromAjax(detail.cart) })
    });
  });

  document.addEventListener('cart-drawer:open', () => {
    if (!window.Shopify || !window.Shopify.StandardEvents) return;
    fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        publishStandardEvent('CartViewEvent', { cart: standardEventCartFromAjax(cart), context: 'dialog' });
      })
      .catch(() => {
        publishStandardEvent('CartViewEvent', { cart: null, context: 'dialog' });
      });
  });

  document.addEventListener('product:variant-change', (event) => {
    const detail = event.detail || {};
    if (!detail.variant || !detail.variant.id) return;
    const payload = {
      promise: Promise.resolve({ variant: { id: detail.variant.id } })
    };
    if (detail.productId) {
      payload.product = { id: detail.productId };
    }
    publishStandardEvent('ProductSelectEvent', payload);
  });

  document.addEventListener('product:variant-error', (event) => {
    const detail = event.detail || {};
    publishStandardEvent('CartErrorEvent', {
      source: detail.source,
      error: {
        message: detail.message || '',
        description: detail.errors || ''
      }
    });
  });

  document.addEventListener('facets:updated', () => {
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (!SE) return;
    const searchParams = new URLSearchParams(window.location.search);
    if (theme.settings.collection_id) {
      publishStandardEvent('CollectionUpdateEvent', {
        collection: { id: theme.settings.collection_id },
        filters: SE.CollectionUpdateEvent.parseProductFilters(searchParams),
        sortKey: SE.CollectionUpdateEvent.getSortKey(searchParams)
      });
    } else if (window.location.pathname.includes(theme.routes.search_url)) {
      publishStandardEvent('SearchUpdateEvent', {
        searchTerm: searchParams.get('q') || '',
        filters: SE.SearchUpdateEvent.parseProductFilters(searchParams),
        sortKey: SE.SearchUpdateEvent.getSortKey(searchParams)
      });
    }
  });
})();
window.addEventListener('message', function(event) {
  var data = event.data;

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      return;
    }
  }

  if (!data || data.id !== 'product-overview-iframe' || !data.height) {
    return;
  }

  var iframe = document.getElementById('product-overview-iframe');

  if (!iframe) {
    return;
  }

  var height = parseInt(data.height, 10);

  if (!height || height < 100) {
    return;
  }

  iframe.style.height = height + 'px';
  iframe.setAttribute('height', height);
  iframe.setAttribute('scrolling', 'no');
});